import { useIntl } from 'react-intl';

import { buildClosestPointTooltipBodyProps } from './closestPointTooltipBodyUtils.js';
import { buildClosestPointTooltipHeaderProps } from './closestPointTooltipHeaderUtils.js';
import { OverlayTooltip } from '../../../../core/components/overlay-tooltip/OverlayTooltip.js';
import type { SeriesItemProps } from '../../../../core/components/overlay-tooltip/types/series.js';
import { useAreaData } from '../../../hooks/useAreaData.js';
import { useAxesFormatter } from '../../../hooks/useAxesFormatter.js';
import { useXYChartData } from '../../../hooks/useXYChartData.js';
import { useXYChartTooltip } from '../../../hooks/useXYChartTooltip.js';
import { useXYChartTruncationMode } from '../../../hooks/useXYChartTruncationMode.js';
import type {
  ClosestDatapoint,
  ClosestSeries,
  ClosestSeriesToMetadata,
} from '../../../utils/type-guards.js';

export function ClosestPointTooltip<T extends ClosestSeries>({
  datapoint,
  series,
  seriesToMetadata,
}: Readonly<{
  datapoint: ClosestDatapoint<T>;
  series: T;
  seriesToMetadata: ClosestSeriesToMetadata<T>;
}>) {
  const axisIdToFormatter = useAxesFormatter();
  const { axisIdToScaleDomain, datapointMetadataMap } = useXYChartData();
  const intl = useIntl();
  const { filteredSeriesWithDatapoints } = useAreaData();
  const { truncationMode } = useXYChartTruncationMode();
  const { seriesDisplayMode } = useXYChartTooltip();

  const { name, nameSegments, color, actions, shape } =
    buildClosestPointTooltipHeaderProps({
      datapoint,
      series,
      seriesToMetadata,
      seriesWithDatapoints: filteredSeriesWithDatapoints,
      datapointMetadataMap,
    });
  const { xAxisValue, yAxisValue, xAxisDuration, yAxisDuration } =
    buildClosestPointTooltipBodyProps({
      datapoint,
      series,
      seriesToMetadata,
      axisIdToFormatter,
      axisIdToScaleDomain,
      datapointMetadataMap,
      intl,
    });

  const isMultiLine =
    nameSegments !== undefined && seriesDisplayMode === 'multi-line';
  const displayName: string | string[] = isMultiLine ? nameSegments : name;

  const data: SeriesItemProps[] = [
    {
      name: displayName,
      value: `${yAxisValue} ${yAxisDuration ? `(${yAxisDuration})` : ''}`,
      color,
      shape,
      multiLine: isMultiLine,
    },
  ];
  const label = datapoint.x0 instanceof Date ? '' : undefined;

  return (
    <OverlayTooltip truncation={truncationMode}>
      <OverlayTooltip.Header
        header1={xAxisValue}
        duration1={xAxisDuration}
        label={label}
      />
      <OverlayTooltip.Series
        data={data}
        variant="shared"
        activeItem={-1}
        seriesItemTemplate={(item) => (
          <OverlayTooltip.SeriesItem {...item} actions={actions} />
        )}
      />
    </OverlayTooltip>
  );
}
